import { aperture, pickBy } from 'ramda';
import { PipeId, ProjectDto, StopePipeArray } from 'providers/api';
import Vector from '../Nodes/Vector';
import PCNode from '../Nodes/PCNode';
import { BOOSTER_SUFFIX, CHOKE_SUFFIX, isSyntheticNodeId } from '../Nodes';
import PCPipe from '../Pipes/PCPipe';
import PCRoute from './PCRoute';
import PCSpool from '../Spools/PCSpool';
import PCStope from '../Stopes/PCStope';
import { isSpoolActive, routableFlow } from '../Spools/utils';
import { missingPipeError, noPipeArraysError } from './errors';

function findNodesInSpoolList(route: PCSpool[], includeNodes: PCNode[]) {
  const inclPointIds = includeNodes.map((n) => n.id);
  let toFindCount = includeNodes.length;
  for (let i = 0; i < inclPointIds.length; i += 1) {
    const pointId = inclPointIds[i];
    const foundSpoolArr = route.filter((s) => (s.pointA.id === pointId || s.pointB.id === pointId));
    if (foundSpoolArr.length > 0) toFindCount -= 1;
  }
  return toFindCount === 0;
}

export function filterRoutesByIncludedNodes(
  routes: Record<string, PCRoute>,
  includeNodes: PCNode[],
) {
  const filteredRoutes: Record<string, PCRoute> = {};
  if (includeNodes.length > 0) {
    const filteredRouteArr = Object.values(routes).filter((route) => findNodesInSpoolList(route.spools, includeNodes));
    filteredRouteArr.forEach((r, id) => {
      filteredRoutes[id] = r;
    });
    return filteredRoutes;
  }
  return routes;
}

function findSpool(spools: PCSpool[], pointAId: string, pointBId: string): PCSpool | undefined {
  return spools.find((spool) => (spool.pointA.id === pointAId && spool.pointB.id === pointBId)
    || (spool.pointA.id === pointBId && spool.pointB.id === pointAId));
}

function routeListToSpools(nodeRoute: string[], spoolArray: PCSpool[]) {
  const spoolList: PCSpool[] = [];
  for (let i = 0; i < nodeRoute.length - 1; i += 1) {
    const pointAId = nodeRoute[i];
    const pointBId = nodeRoute[i + 1];
    const spool = findSpool(spoolArray, pointAId, pointBId);
    if (spool) {
      spoolList.push(spool);
    } else {
      // eslint-disable-next-line no-console
      console.error(`Spool not found for connection: ${pointAId} -> ${pointBId}`);
    }
  }
  return spoolList;
}

// PipeId arrives either as the wrapped { value } form or as a bare string depending on how the
// document was serialised, so unwrap before comparing or reporting.
function pipeIdToString(pipeID: PipeId): string {
  if (!pipeID) return '';
  return String((pipeID as any).value ?? pipeID);
}

function getMatchingPipe(pipes: PCPipe[], pipeID: PipeId): PCPipe | undefined {
  if (!pipeID) return undefined;
  const idValue = pipeIdToString(pipeID);
  return pipes.find((pipe) => String(pipe.pipeId) === idValue);
}

const dischargeNodePrefix = (stope: PCStope): string => `${stope.data.stopeId}-discharge-`;

function generateDischargeNode(stope: PCStope, pipe: StopePipeArray, previousNode: PCNode, nodeIndex: string): PCNode {
  return new PCNode({
    coords: new Vector(
      previousNode.coords.x + (pipe.pipeHorizontalLength ?? 0),
      previousNode.coords.y,
      previousNode.coords.z + (pipe.pipeVerticalLength ?? 0),
    ),
    id: `${dischargeNodePrefix(stope)}${nodeIndex}`,
    pipeId: pipe.pipeId as string,
  });
}

function getAllDischargeSpools(stope: PCStope, pipeList: PCPipe[]): PCSpool[] {
  if (!stope.data.pipeArrays || stope.data.pipeArrays.length === 0) {
    throw noPipeArraysError(String(stope.data.stopeId));
  }

  const dischargeSpools: PCSpool[] = [];
  let currentNode: PCNode = stope.originNode;

  for (let i = 0; i < stope.data.pipeArrays.length; i += 1) {
    const pa = stope.data.pipeArrays[i];
    const pipe = getMatchingPipe(pipeList, pa.pipeId as PipeId);
    if (!pipe) throw missingPipeError(String(stope.data.stopeId), pipeIdToString(pa.pipeId as PipeId));

    const dischargeNode = generateDischargeNode(stope, pa, currentNode, String(i));

    const spool = new PCSpool(
      `dischargeSpool-${i}`,
    0,
    currentNode,
    dischargeNode,
    pipe,
    true,
    true,
  );

    dischargeSpools.push(spool);
    currentNode = dischargeNode;
  }

  return dischargeSpools.reverse();
}

function removeConsecutiveDuplicates(list: string[]): string[] {
  return list.filter((id, i) => i === 0 || id !== list[i - 1]);
}

function orderSpoolsByRoute(spools: PCSpool[], routePointStrings: string[]): PCSpool[] {
  const ordered: PCSpool[] = [];

  for (let i = 0; i < routePointStrings.length - 1; i += 1) {
    const from = routePointStrings[i];
    const to = routePointStrings[i + 1];

    const spool = spools.find((s) => (s.pointA.id === from && s.pointB.id === to)
      || (s.pointA.id === to && s.pointB.id === from));

    if (!spool) {
      console.error('routePointStrings:', routePointStrings);
      console.error('spools:', spools);
      throw new Error(`Cannot order spools by route — missing spool for segment: ${from} → ${to}`);
    }

    ordered.push(spool);
  }

  return ordered;
}

function prepareSpoolsForRoute(
  rawSpools: PCSpool[],
  rawRoutePoints: string[],
): { orderedSpools: PCSpool[]; cleanRoutePoints: string[] } {
  const cleanRoutePoints = removeConsecutiveDuplicates(rawRoutePoints);
  const ordered = orderSpoolsByRoute(rawSpools, cleanRoutePoints);
  return {
    orderedSpools: ordered,
    cleanRoutePoints,
  };
}

export type NeighborIndex = Map<string, Array<{ id: string; w: number }>>;
type WeightIndex = Map<string, number>;
const edgeKey = (a: string, b: string) => `${a}→${b}`;

export function buildGraphIndexes(spools: Record<string, PCSpool>, biDirectionalBoreholes: boolean): {
  neighbors: NeighborIndex;
  weights: WeightIndex;
} {
  const neighbors: NeighborIndex = new Map();
  const weights: WeightIndex = new Map();

  const addNeighbor = (u: string, v: string, w: number) => {
    if (!neighbors.has(u)) neighbors.set(u, []);
    neighbors.get(u)!.push({ id: v, w });
    weights.set(edgeKey(u, v), w);
  };

  Object.values(spools).forEach((s) => {
    const len = s.length();
    const { allowAB, allowBA } = routableFlow(s, biDirectionalBoreholes);
    if (allowAB) addNeighbor(s.pointB.id, s.pointA.id, len);
    if (allowBA) addNeighbor(s.pointA.id, s.pointB.id, len);
    if (!neighbors.has(s.pointA.id)) neighbors.set(s.pointA.id, []);
    if (!neighbors.has(s.pointB.id)) neighbors.set(s.pointB.id, []);
  });

  return { neighbors, weights };
}

class MinPQ<T> {
  private h: Array<{ k: number; v: T }> = [];

  push(k: number, v: T) {
    const heap = this.h;
    heap.push({ k, v });
    let i = heap.length - 1;
    while (i > 0) {
      const p = Math.floor((i - 1) / 2);
      if (heap[p].k <= heap[i].k) break;
      [heap[p], heap[i]] = [heap[i], heap[p]];
      i = p;
    }
  }

  pop(): T | undefined {
    const heap = this.h;
    if (heap.length === 0) return undefined;
    const top = heap[0];
    const last = heap.pop()!;
    if (heap.length > 0) {
      heap[0] = last;
      let i = 0;
      const n = heap.length;
      while (true) {
        let smallest = i;
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        if (l < n && heap[l].k < heap[smallest].k) smallest = l;
        if (r < n && heap[r].k < heap[smallest].k) smallest = r;
        if (smallest === i) break;
        [heap[i], heap[smallest]] = [heap[smallest], heap[i]];
        i = smallest;
      }
    }
    return top.v;
  }

  get size() {
    return this.h.length;
  }
}

export function dijkstraShortestPath(
  neighbors: NeighborIndex,
  start: string,
  goal: string,
  heuristic?: Map<string, number>,
): { path: string[]; dist: number; visitedNodes?: Set<string> } | undefined {
  const dist = new Map<string, number>();
  const prev = new Map<string, string>();
  const settled = new Set<string>();
  const pq = new MinPQ<string>();

  dist.set(start, 0);
  pq.push(heuristic?.get(start) ?? 0, start);

  while (pq.size) {
    const u = pq.pop()!;
    if (!settled.has(u)) {
      settled.add(u);
      if (u === goal) break;
      (neighbors.get(u) || []).forEach(({ id: v, w }) => {
        const alt = dist.get(u)! + w;
        if (alt < (dist.get(v) ?? Infinity)) {
          dist.set(v, alt);
          prev.set(v, u);
          pq.push(alt + (heuristic?.get(v) ?? 0), v);
        }
      });
    }
  }

  const visitedNodes = new Set<string>(dist.keys());

  if (start !== goal && !prev.has(goal)) {
    return { path: [], dist: Infinity, visitedNodes };
  }

  const path: string[] = [];
  if (start === goal) return { path: [start], dist: 0, visitedNodes };
  let cur: string | undefined = goal;
  while (cur && cur !== start) {
    path.push(cur);
    cur = prev.get(cur);
  }
  if (!cur) return { path: [], dist: Infinity, visitedNodes };
  path.push(start);
  path.reverse();
  return { path, dist: dist.get(goal)!, visitedNodes };
}

function computeReverseDistances(
  neighbors: NeighborIndex,
  goal: string,
): Map<string, number> {
  const reverseNeighbors: NeighborIndex = new Map();
  neighbors.forEach((edges, u) => {
    if (!reverseNeighbors.has(u)) reverseNeighbors.set(u, []);
    edges.forEach(({ id: v, w }) => {
      if (!reverseNeighbors.has(v)) reverseNeighbors.set(v, []);
      reverseNeighbors.get(v)!.push({ id: u, w });
    });
  });

  const dist = new Map<string, number>();
  const settled = new Set<string>();
  const pq = new MinPQ<string>();

  reverseNeighbors.forEach((_v, node) => dist.set(node, Infinity));
  neighbors.forEach((_v, node) => {
    if (!dist.has(node)) dist.set(node, Infinity);
  });
  dist.set(goal, 0);
  pq.push(0, goal);

  while (pq.size > 0) {
    const u = pq.pop()!;
    if (!settled.has(u)) {
      settled.add(u);
      const du = dist.get(u)!;
      (reverseNeighbors.get(u) || []).forEach(({ id: v, w }) => {
        const alt = du + w;
        if (alt < (dist.get(v) ?? Infinity)) {
          dist.set(v, alt);
          pq.push(alt, v);
        }
      });
    }
  }

  return dist;
}

// Required, so every route builder (stopes page, recipes, stope note) routes boreholes by the same rule.
interface RouteOpts {
  k?: number;
  maxStretch?: number;
  maxHops?: number;
  biDirectionalBoreholes: boolean;
}

export const routeOptsForProject = (project: ProjectDto | undefined): RouteOpts => ({
  biDirectionalBoreholes: !!project?.biDirectionalBoreholes,
});

function hopCountOk(path: string[], maxHops?: number) {
  return !maxHops || path.length - 1 <= maxHops;
}

function pathKey(path: string[]): string {
  return path.join('|');
}

function prefixMatches(path: string[], rootPath: string[], len: number): boolean {
  for (let j = 0; j < len; j += 1) {
    if (path[j] !== rootPath[j]) return false;
  }
  return true;
}

function yensKShortestPathsPruned(
  neighborsBase: NeighborIndex,
  weights: WeightIndex,
  start: string,
  goal: string,
  k: number,
  opts: RouteOpts,
): Array<{ path: string[]; dist: number; visitedNodes?: Set<string> }> {
  const heuristic = computeReverseDistances(neighborsBase, goal);
  const result: Array<{ path: string[]; dist: number; visitedNodes?: Set<string> }> = [];
  const first = dijkstraShortestPath(neighborsBase, start, goal, heuristic);
  if (!first || first.path.length === 0) return result;
  if (!hopCountOk(first.path, opts.maxHops)) return result;
  result.push(first);

  const shortestDist = first.dist;
  const maxDist = opts.maxStretch ? shortestDist * opts.maxStretch : Infinity;

  const candidateKeys = new Set<string>();
  const candidates: Array<{ path: string[]; dist: number }> = [];

  for (let i = 1; i < k; i += 1) {
    const last = result[i - 1].path;

    let rootCost = 0;
    for (let spurIdx = 0; spurIdx < last.length - 1; spurIdx += 1) {
      if (spurIdx > 0) {
        rootCost += weights.get(edgeKey(last[spurIdx - 1], last[spurIdx])) ?? Infinity;
      }

      if (rootCost > maxDist) break;

      const prefixLen = spurIdx + 1;
      const edgesRemoved: Array<{ node: string; neighbor: { id: string; w: number }; idx: number }> = [];

      result.forEach((p) => {
        if (p.path.length > spurIdx + 1 && prefixMatches(p.path, last, prefixLen)) {
          const u = p.path[spurIdx];
          const v = p.path[spurIdx + 1];
          const lst = neighborsBase.get(u);
          if (lst) {
            const idx = lst.findIndex((n) => n.id === v);
            if (idx >= 0) {
              edgesRemoved.push({ node: u, neighbor: lst[idx], idx });
              lst.splice(idx, 1);
            }
          }
        }
      });

      const nodesRemoved: Array<{ node: string; backup: Array<{ id: string; w: number }> }> = [];
      for (let j = 0; j < spurIdx; j += 1) {
        const n = last[j];
        const lst = neighborsBase.get(n);
        if (lst && lst.length > 0) {
          nodesRemoved.push({ node: n, backup: lst });
          neighborsBase.set(n, []);
        }
      }

      const spurNode = last[spurIdx];
      const spur = dijkstraShortestPath(neighborsBase, spurNode, goal, heuristic);

      nodesRemoved.forEach((r) => {
        neighborsBase.set(r.node, r.backup);
      });
      for (let j = edgesRemoved.length - 1; j >= 0; j -= 1) {
        const { node, neighbor, idx } = edgesRemoved[j];
        neighborsBase.get(node)!.splice(idx, 0, neighbor);
      }

      if (spur && spur.path.length > 1) {
        const combined = [...last.slice(0, spurIdx), ...spur.path];
        const dist = rootCost + spur.dist;
        if (dist <= maxDist && hopCountOk(combined, opts.maxHops)) {
          const key = pathKey(combined);
          if (!candidateKeys.has(key)) {
            candidateKeys.add(key);
            candidates.push({ path: combined, dist });
          }
        }
      }
    }

    if (candidates.length === 0) break;
    candidates.sort((a, b) => a.dist - b.dist);
    result.push(candidates.shift()!);
  }

  return result;
}

// A booster pump resets the HGL to zero at a node, a choke station adds a fixed head loss there. Both are
// rendered by injecting a synthetic twin node at the original's coordinates: a zero-length spool joins the node
// to its twin, and a second spool replaces the original node->next segment. The route therefore carries two
// points at the same distance-from-pump with different pressures — a vertical HGL step — while routeLength is
// unchanged because the replacement segment keeps the original geometry.
function injectPressureDiscontinuityNodes(
  path: string[],
  nodes: Record<string, PCNode>,
  pipes: Record<string, PCPipe>,
  spoolArray: PCSpool[],
): { enhancedPath: string[]; discontinuitySpools: PCSpool[] } {
  const enhancedPath: string[] = [];
  const discontinuitySpools: PCSpool[] = [];

  path.forEach((nodeId, index) => {
    enhancedPath.push(nodeId);

    const node = nodes[nodeId];
    const nextNodeId = path[index + 1];
    if (!node || !nextNodeId) return;

    // Booster and choke are mutually exclusive on a node (enforced by the node form and the backend), but
    // resolve a single suffix defensively so a legacy node carrying both cannot produce two twins.
    const suffix = (() => {
      if (node.boosterPressure > 0) return BOOSTER_SUFFIX;
      if (node.chokeHead > 0) return CHOKE_SUFFIX;
      return null;
    })();
    if (!suffix) return;

    const twinNodeId = `${nodeId}${suffix}`;
    const twinNode = nodes[twinNodeId];
    if (!twinNode) return;

    enhancedPath.push(twinNodeId);

    const nextNode = nodes[nextNodeId];
    // The twin spools stand in for the real node->next segment, so they carry that spool's own pipe.
    const pipe = findSpool(spoolArray, nodeId, nextNodeId)?.pipe ?? pipes[node.pipeId];
    if (!pipe) return;

    // Zero-length spool from the original node to its twin
    discontinuitySpools.push(new PCSpool(
      `${nodeId}-${twinNodeId}`,
      0,
      node,
      twinNode,
      pipe,
      true,
      true,
    ));

    // Also create spool from the twin node to the next node in path (if exists)
    if (nextNode) {
      discontinuitySpools.push(new PCSpool(
        `${twinNodeId}-${nextNodeId}`,
        0,
        twinNode,
        nextNode,
        pipe,
        true,
        true,
      ));
    }
  });

  return { enhancedPath, discontinuitySpools };
}

function convertGraphRouteToPCRoute(
  kpaths: Array<{ path: string[]; dist: number }>,
  spoolArray: PCSpool[],
  stope: PCStope,
  pipeList: PCPipe[],
  nodes: Record<string, PCNode>,
  pipes: Record<string, PCPipe>,
): Record<string, PCRoute> {
  const PCRoutes: Record<string, PCRoute> = {};
  const dischargeSpools = getAllDischargeSpools(stope, pipeList);
  const dischargeNodeIds = dischargeSpools.map((spool) => spool.pointB.id);

  kpaths.forEach((k, idx) => {
    // Inject _booster / _choke nodes into the path after pathfinding
    const { enhancedPath, discontinuitySpools } = injectPressureDiscontinuityNodes(k.path, nodes, pipes, spoolArray);

    // Include the zero-length twin spools in the array so routeListToSpools can find them
    const enhancedSpoolArray = [...spoolArray, ...discontinuitySpools];
    const routeSpools = routeListToSpools(enhancedPath, enhancedSpoolArray);
    const rawRoutePoints = [...dischargeNodeIds, ...enhancedPath];
    const rawSpools = [...dischargeSpools, ...routeSpools];
    const { orderedSpools, cleanRoutePoints } = prepareSpoolsForRoute(rawSpools, rawRoutePoints);
    const routeLength = orderedSpools.reduce((sum, s) => sum + s.length(), 0);
    PCRoutes[idx] = new PCRoute(
      stope.originNode,
      stope,
      orderedSpools,
      routeLength,
      cleanRoutePoints,
    );
  });

  return PCRoutes;
}

export function generateAllRoutes(
  pastePlantNode: PCNode,
  spools: Record<string, PCSpool>,
  stope: PCStope,
  pipeList: PCPipe[],
  opts: RouteOpts,
  nodes?: Record<string, PCNode>,
  pipes?: Record<string, PCPipe>,
): { routeObjects: Record<string, PCRoute>; visitedSpools: PCSpool[] } {
  const startNode = pastePlantNode.id;
  const endNode = stope.originNode?.id ?? '';
  if (!endNode) {
    throw new Error('Stope has no origin node');
  }
  // A route search never crosses an inactive spool; a stored route over one is redrawn by rebuildStoredRoute.
  const routable: Record<string, PCSpool> = pickBy(isSpoolActive, spools);
  const spoolArray = Object.values(routable);

  const { neighbors, weights } = buildGraphIndexes(routable, opts.biDirectionalBoreholes);
  const from = endNode;
  const to = startNode;
  const K = Math.max(1, opts.k ?? 50);

  const kpaths = K === 1
    ? (() => {
      const one = dijkstraShortestPath(neighbors, from, to);
      return one && one.path.length > 0 ? [one] : [];
    })()
    : yensKShortestPathsPruned(neighbors, weights, from, to, K, opts);

  const nodesMap = nodes ?? {};
  const pipesMap = pipes ?? {};
  const routeObjects = convertGraphRouteToPCRoute(kpaths, spoolArray, stope, pipeList, nodesMap, pipesMap);

  let visitedSpools: PCSpool[] = [];
  if (kpaths.length === 0 || (kpaths.length === 1 && kpaths[0].path.length === 0)) {
    const visitedNodes = kpaths.length > 0 && kpaths[0].visitedNodes
      ? kpaths[0].visitedNodes
      : (() => {
        const searchResult = dijkstraShortestPath(neighbors, from, to);
        return searchResult?.visitedNodes ?? new Set<string>();
      })();

    visitedSpools = spoolArray.filter((spool) => visitedNodes.has(spool.pointA.id) && visitedNodes.has(spool.pointB.id));
  }

  return { routeObjects, visitedSpools };
}

// Redraws a stored stope -> plant walk without a route search, inactive spools included; undefined once any part of it is gone.
export const rebuildStoredRoute = (
  walk: string[],
  routeSpoolIds: string[] | undefined,
  stope: PCStope,
  spools: Record<string, PCSpool>,
  nodes: Record<string, PCNode>,
  pipes: Record<string, PCPipe>,
): PCRoute | undefined => {
  const discharge = dischargeNodePrefix(stope);
  const path = walk.filter((id) => !id.startsWith(discharge) && !isSyntheticNodeId(id));
  if (path.length < 2 || path[0] !== stope.originNode?.id || !path.every((id) => !!nodes[id])) return undefined;

  // The stored spools go first, so where parallel spools join two nodes the route keeps the one it was planned on.
  const storedSpools = (routeSpoolIds ?? []).flatMap((id) => (spools[id] ? [spools[id]] : []));
  const spoolArray = [...storedSpools, ...Object.values(spools)];
  if (!aperture(2, path).every(([from, to]) => !!findSpool(spoolArray, from, to))) return undefined;

  return convertGraphRouteToPCRoute([{ path, dist: 0 }], spoolArray, stope, Object.values(pipes), nodes, pipes)[0];
};

export function returnShortestRoute(routes: Record<string, PCRoute>): PCRoute | undefined {
  let shortestRoute: PCRoute | undefined;
  Object.values(routes).forEach((route) => {
    if (shortestRoute === undefined || route.routeLength < shortestRoute.routeLength) {
      shortestRoute = route;
    }
  });
  return shortestRoute;
}

export const isRouteActive = (route: PCRoute): boolean => route.spools.every(isSpoolActive);

// Every new plan runs on the shortest all-active route; `routes` may still hold a redrawn stored route.
export const liveRouteFor = (routes: Record<string, PCRoute>): PCRoute | undefined => returnShortestRoute(pickBy(isRouteActive, routes));
